import * as THREE from 'three';

import { createFrameExtentPointCollector } from '@toolproof-core/visualization/rendering/frame-extent-point-collector';
import type { CameraGraph, CameraStation, CameraView } from '@toolproof-core/visualization/_contract/camera';
import type { Vector3Tuple } from '@toolproof-core/visualization/_contract/geometry';
import type { SpaceDerivationFrame } from '@toolproof-core/visualization/derivation/_contract/derivation';

import { reduceCameraEnvelope } from '@/adapters/primitive/_pure/derivation/cameraEnvelope';
import { derivePrimitiveCameraFocus, selectPrimitiveCameraWheels } from '@/adapters/primitive/_pure/derivation/cameraFocus';
import { fitPrimitiveCameraOffsetToWheelPower, toPrimitiveCameraViewId,
    toPrimitiveStandoffStationId } from '@/adapters/primitive/_pure/derivation/cameraGraph';
import { toPrimitivePowerConstellationKey } from '@/adapters/primitive/_pure/derivation/keyCodec';
import { buildPrimitiveRodFrame, toPrimitiveRodPlacementBasis, type PrimitiveRodPlacement } from '@/adapters/primitive/_pure/derivation/primitiveRod';
import { toScopedPrimitiveRodKey } from '@/adapters/primitive/_pure/derivation/primitiveRod';
import { toPrimitiveStepWheels } from '@/adapters/primitive/_pure/derivation/primitiveSteps';
import { toPrimitiveCameraSteps } from '@/adapters/primitive/_pure/derivation/wheels';
import type { PrimitiveCameraConfig, PrimitiveCameraFocus } from '@/adapters/primitive/_contract/camera';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';


function envelopeCenter(points: readonly Vector3Tuple[]): Vector3Tuple {
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (const point of points) for (let axis = 0; axis < 3; axis++) {
        min[axis] = Math.min(min[axis]!, point[axis]!);
        max[axis] = Math.max(max[axis]!, point[axis]!);
    }
    return [(min[0]! + max[0]!) / 2, (min[1]! + max[1]!) / 2, (min[2]! + max[2]!) / 2];
}


export function derivePrimitiveCameraGraph(input: {
    spaceKey: SpaceDerivationFrame['spaceKey'];
    config: Config;
    camera: PrimitiveCameraConfig;
    steps: readonly PrimitiveStepState[];
    scope: string;
    placement?: PrimitiveRodPlacement;
    postPlacementOverride?: (wheel: PrimitiveWheelState) => PrimitiveWheelState;
    /** Frame every wheel in the overview, even when the rod extends behind the opening wheels. */
    fullOverview?: boolean;
    /** Workflow Boolean graphs supply their rigid unfolded placement and geometry. */
    buildFocusFrame?: (step: PrimitiveStepState, wheels: readonly PrimitiveWheelState[],
        booleanRod: PrimitiveStepState['keyframes'][number]['booleanRod']) => SpaceDerivationFrame;
}): { graph: CameraGraph; viewByStepIndex: readonly string[] } {
    const args = { ...input, steps: toPrimitiveCameraSteps(input.steps) };
    const { camera } = args;
    if (!['guided', 'free'].includes(camera.base.navigationMode) || !Number.isFinite(camera.base.padding) || camera.base.padding < 1
        || !Number.isFinite(camera.base.fov) || camera.base.fov <= 0 || camera.base.fov >= 180
        || !Number.isFinite(camera.base.navigationDurationMs) || camera.base.navigationDurationMs < 0
        || !Number.isFinite(camera.natural.dispatchHoldDurationMs) || camera.natural.dispatchHoldDurationMs < 0
        || [camera.base.overviewDirection, camera.natural.resultSettlementOffset, camera.natural.compositeSettlementOffset, camera.boolean.graphInspectionDirection].some((direction) =>
            direction.length !== 3 || !direction.every(Number.isFinite) || Math.hypot(...direction) === 0)) {
        throw new Error('Invalid primitive camera configuration.');
    }
    const stations: Record<string, CameraStation> = {};
    const views: Record<string, CameraView> = {};
    const basis = args.placement ? toPrimitiveRodPlacementBasis(args.placement)
        : { tangent: new THREE.Vector3(1, 0, 0), up: new THREE.Vector3(0, 1, 0), axis: new THREE.Vector3(0, 0, 1) };
    type Envelope = { min: Vector3Tuple; max: Vector3Tuple };
    const emptyEnvelope = (): Envelope => ({ min: [Infinity, Infinity, Infinity], max: [-Infinity, -Infinity, -Infinity] });
    const include = (envelope: Envelope, points: readonly Vector3Tuple[]) => {
        const vector = new THREE.Vector3();
        for (const point of points) {
            vector.set(...point);
            const local = [vector.dot(basis.tangent), vector.dot(basis.up), vector.dot(basis.axis)];
            for (let axis = 0; axis < 3; axis++) {
                envelope.min[axis] = Math.min(envelope.min[axis]!, local[axis]!);
                envelope.max[axis] = Math.max(envelope.max[axis]!, local[axis]!);
            }
        }
    };
    const extentPoints = (envelope: Envelope): Vector3Tuple[] => [envelope.min[0], envelope.max[0]].flatMap((x) =>
        [envelope.min[1], envelope.max[1]].flatMap((y) => [envelope.min[2], envelope.max[2]].map((z) =>
            basis.tangent.clone().multiplyScalar(x).addScaledVector(basis.up, y).addScaledVector(basis.axis, z).toArray())));
    const groups = new Map<string, {
        envelope: Envelope; points: Map<string, Vector3Tuple>; included: Set<string>;
        targets: Map<string, Vector3Tuple>; maximumPower: number;
    }>();
    const geometryCache = new Map<string, { frame: SpaceDerivationFrame; points: Vector3Tuple[]; overviewPoints: Vector3Tuple[] }>();
    const collectOverviewPoints = createFrameExtentPointCollector();
    const collectBillboardPoints = createFrameExtentPointCollector();
    const frameGeometryCache = new Map<string, {
        frame: SpaceDerivationFrame; points: Vector3Tuple[]; overviewPoints: Vector3Tuple[];
    }>();
    const focusFrameStepIndex = args.buildFocusFrame
        ? new Map(args.steps.map((step, index) => [step, index])) : undefined;
    const wholeEnvelope = emptyEnvelope();
    const viewByStepIndex: string[] = [];
    const billboardDirection = basis.tangent.clone().multiplyScalar(camera.boolean.graphInspectionDirection[0])
        .addScaledVector(basis.up, camera.boolean.graphInspectionDirection[1]).addScaledVector(basis.axis, camera.boolean.graphInspectionDirection[2]).toArray();
    let previous: readonly PrimitiveWheelState[] = [];
    const makeFrame = (step: PrimitiveStepState, wheels: readonly PrimitiveWheelState[],
        booleanRod: PrimitiveStepState['keyframes'][number]['booleanRod']) => args.buildFocusFrame
        ? args.buildFocusFrame(step, wheels, booleanRod)
        : buildPrimitiveRodFrame({
            spaceKey: args.spaceKey, config: args.config, wheels, scope: args.scope,
            ...(booleanRod ? { booleanRod } : {}),
            ...(args.placement ? { placement: args.placement } : {}),
            ...(args.postPlacementOverride ? { postPlacementOverride: args.postPlacementOverride } : {}),
        });
    const geometryKey = (step: PrimitiveStepState, wheels: readonly PrimitiveWheelState[],
        booleanRod: PrimitiveStepState['keyframes'][number]['booleanRod']) => JSON.stringify({
            step: focusFrameStepIndex?.get(step),
            wheels: wheels.map((wheel) => {
                const geometry = { ...wheel };
                delete geometry.wheelOpacity;
                delete geometry.hubOpacity;
                delete geometry.bandOpacityByPower;
                delete geometry.bandLabels;
                return geometry;
            }),
            rod: booleanRod,
        });
    const getFrameGeometry = (step: PrimitiveStepState, wheels: readonly PrimitiveWheelState[],
        booleanRod: PrimitiveStepState['keyframes'][number]['booleanRod']) => {
        const key = geometryKey(step, wheels, booleanRod);
        let geometry = frameGeometryCache.get(key);
        if (!geometry) {
            const frame = makeFrame(step, wheels, booleanRod);
            const envelope = emptyEnvelope();
            include(envelope, collectOverviewPoints(frame));
            const overviewPoints = envelope.min.every(Number.isFinite) ? extentPoints(envelope) : [];
            geometry = {
                frame: { spaceKey: frame.spaceKey, constellations: [], itemCollections: [],
                    ...(frame.constellationParametricState ? { constellationParametricState: frame.constellationParametricState } : {}) },
                points: booleanRod ? collectBillboardPoints(frame, { billboardDirection }) : overviewPoints,
                overviewPoints,
            };
            frameGeometryCache.set(key, geometry);
        }
        return geometry;
    };
    const addFrame = (group: { envelope: Envelope; points: Map<string, Vector3Tuple>;
        included: Set<string>; maximumPower: number }, step: PrimitiveStepState,
        wheels: readonly PrimitiveWheelState[], focus: PrimitiveCameraFocus,
        booleanRod: PrimitiveStepState['keyframes'][number]['booleanRod']) => {
        const selected = wheels.filter((wheel) => focus.roles.includes(wheel.role)
            || wheel.receiverLocation?.receiverRole === focus.targetRole);
        const workingWheels = selectPrimitiveCameraWheels(step, wheels, focus);
        // Hidden geometry is included, so opacity-only changes can reuse the same envelope.
        const selectedKey = geometryKey(step, selected, booleanRod);
        const useWorkingFrame = focus.power !== undefined && focus.stationKey.startsWith('working:');
        const focusedWheels = useWorkingFrame ? workingWheels : selected;
        group.maximumPower = Math.max(group.maximumPower, ...focusedWheels.flatMap((wheel) => wheel.powers));
        const key = JSON.stringify([selectedKey, useWorkingFrame ? geometryKey(step, workingWheels, booleanRod) : selectedKey]);
        let entry = geometryCache.get(key);
        if (!entry) {
            const overviewGeometry = getFrameGeometry(step, selected, booleanRod);
            const workingGeometry = useWorkingFrame
                ? getFrameGeometry(step, workingWheels, booleanRod) : overviewGeometry;
            entry = {
                frame: workingGeometry.frame,
                points: booleanRod ? overviewGeometry.points : workingGeometry.points,
                overviewPoints: overviewGeometry.overviewPoints,
            };
            geometryCache.set(key, entry);
        }
        if (!group.included.has(key)) {
            include(group.envelope, booleanRod ? entry.overviewPoints : entry.points);
            include(wholeEnvelope, entry.overviewPoints);
            if (booleanRod) for (const point of entry.points) group.points.set(JSON.stringify(point), point);
            group.included.add(key);
        }
        return entry.frame;
    };
    for (const step of args.steps) {
        const focus = derivePrimitiveCameraFocus(step, previous);
        const stationId = toPrimitiveCameraViewId(args.scope, focus.stationKey);
        const viewId = toPrimitiveCameraViewId(args.scope, focus.viewKey);
        let group = groups.get(stationId);
        if (!group) {
            group = { envelope: emptyEnvelope(), points: new Map(), included: new Set(), targets: new Map(), maximumPower: 0 };
            groups.set(stationId, group);
        }
        if (previous.length > 0) addFrame(group, step, previous, focus, step.keyframes[0]?.booleanRod);
        let last: SpaceDerivationFrame | undefined;
        for (const keyframe of step.keyframes) last = addFrame(group, step, keyframe.wheels, focus, keyframe.booleanRod);
        if (!last || !group.envelope.min.every(Number.isFinite)) throw new Error(`Primitive camera focus has no geometry: ${step.stepKey}`);
        let target: Vector3Tuple | undefined;
        if (focus.targetRole) {
            const key = toScopedPrimitiveRodKey(toPrimitivePowerConstellationKey({
                wheelRole: focus.targetRole, power: focus.power ?? 0,
            }), args.scope);
            const anchor = last.constellationParametricState?.entityConstellationByKey?.[key]?.layout?.anchor;
            if (anchor?.anchorKind === 'position') target = [...anchor.position];
        }
        if (focus.stationKey === 'boolean-graph') {
            // All Boolean branch junctions share the input/output boundary; average their symmetric heights.
            const rod = step.keyframes.find((frame) => frame.booleanRod)?.booleanRod;
            const junctions = rod?.segments.filter((segment) => segment.segmentKey.endsWith(':input-one')).map((segment) => segment.to) ?? [];
            if (junctions.length > 0) {
                const native: Vector3Tuple = [0, 0, 0];
                for (const point of junctions) for (let axis = 0; axis < 3; axis++) native[axis] += point[axis]! / junctions.length;
                if (args.placement) {
                    const { tangent, up, axis } = toPrimitiveRodPlacementBasis(args.placement);
                    target = new THREE.Vector3(...args.placement.origin ?? [0, 0, 0])
                        .addScaledVector(tangent, native[0]).addScaledVector(up, native[1]).addScaledVector(axis, native[2]).toArray();
                } else target = native;
            }
        }
        target ??= envelopeCenter(extentPoints(group.envelope));
        group.targets.set(viewId, target);
        views[viewId] = { station: stationId, target: { anchorKind: 'position', position: target } };
        viewByStepIndex.push(viewId);
        previous = toPrimitiveStepWheels(step);
    }
    const booleanStationId = toPrimitiveCameraViewId(args.scope, 'boolean-graph');
    const placeDirection = (local: Vector3Tuple): Vector3Tuple => basis.tangent.clone().multiplyScalar(local[0])
        .addScaledVector(basis.up, local[1]).addScaledVector(basis.axis, local[2]).toArray();
    const overviewDirection = placeDirection(camera.base.overviewDirection);
    for (const [id, group] of groups) {
        const naturalWorking = id.startsWith(toPrimitiveCameraViewId(args.scope, 'working:'));
        if (naturalWorking) {
            const anchor = { anchorKind: 'position' as const, position: [...group.targets.values()][0]! };
            const resultSettlementOffset = fitPrimitiveCameraOffsetToWheelPower({
                config: args.config, offset: camera.natural.resultSettlementOffset,
                maximumPower: group.maximumPower, fov: camera.base.fov, padding: camera.base.padding,
            });
            const compositeSettlementOffset = fitPrimitiveCameraOffsetToWheelPower({
                config: args.config, offset: camera.natural.compositeSettlementOffset,
                maximumPower: group.maximumPower, fov: camera.base.fov, padding: camera.base.padding,
            });
            stations[id] = {
                stationKind: 'anchored', anchor, offset: placeDirection(resultSettlementOffset), fov: camera.base.fov,
            };
            stations[toPrimitiveStandoffStationId(id)] = {
                stationKind: 'anchored', anchor,
                offset: placeDirection(compositeSettlementOffset), fov: camera.base.fov,
            };
            continue;
        }
        const box = extentPoints(group.envelope);
        const points = id === booleanStationId ? reduceCameraEnvelope([...group.points.values()]) : box;
        const bounds = { center: envelopeCenter(box), points };
        stations[id] = {
            stationKind: 'framing', framing: { framingKind: 'extent', bounds,
                direction: id === booleanStationId ? placeDirection(camera.boolean.graphInspectionDirection)
                    : id === toPrimitiveCameraViewId(args.scope, 'multiplication-comparison')
                        ? placeDirection([1, 0.35, 0])
                        : id === toPrimitiveCameraViewId(args.scope, 'division-comparison-integrated')
                            || id === toPrimitiveCameraViewId(args.scope, 'multiplication-comparison-initial')
                            ? placeDirection([-1, 0.35, 0]) : overviewDirection,
                padding: camera.base.padding },
            ...(id === booleanStationId ? {
                positioningAnchor: views[toPrimitiveCameraViewId(args.scope, 'boolean-junction')]!.target,
            } : {}),
            fov: camera.base.fov,
            requirements: [...group.targets.values()].map((position) => ({ target: { anchorKind: 'position', position }, bounds })),
        };
    }
    // All groups contribute to a stable operation overview, including future helpers and unfolded geometry.
    // Rods reaching beyond NaturalAdd's three stations open with NaturalAdd's framing; deeper stations
    // and sideways comparisons recede or use their own stations.
    const gap = args.config.base.display.wheel.wheelGap;
    const front = (wheels: readonly PrimitiveWheelState[]) => wheels.filter(wheel => wheel.position[2] >= -gap - 1e-6);
    let decisionEnvelope: Envelope | undefined;
    if (!args.placement && !args.fullOverview && args.steps.some(step => step.keyframes.some(frame =>
        frame.wheels.length > front(frame.wheels).length))) {
        const initial = args.steps[0]!;
        const geometry = getFrameGeometry(initial, front(initial.keyframes[0]!.wheels), undefined);
        wholeEnvelope.min = [Infinity, Infinity, Infinity];
        wholeEnvelope.max = [-Infinity, -Infinity, -Infinity];
        include(wholeEnvelope, geometry.overviewPoints);
        // Once Decision rests beside the rod, a wider overview adds every Decision station to the opening.
        for (const step of args.steps) {
            for (const frame of step.keyframes) {
                const decision = frame.wheels.filter(wheel => wheel.role === 'less-than-decision');
                if (decision.length === 0) continue;
                decisionEnvelope ??= { min: [...wholeEnvelope.min], max: [...wholeEnvelope.max] };
                include(decisionEnvelope, getFrameGeometry(step, decision, undefined).overviewPoints);
            }
        }
    }
    if (!args.placement && args.steps.some(step => step.stepKey === 'rod-extend')) {
        const initial = args.steps[0]!;
        const product = initial.keyframes[0]!.wheels.find(wheel => wheel.role === 'product');
        const working = stations[toPrimitiveCameraViewId(args.scope, 'working:product:0')];
        if (product && working?.stationKind === 'anchored') {
            const id = toPrimitiveCameraViewId(args.scope, 'working:product:initial');
            const anchor = { anchorKind: 'position' as const, position: [...product.position] as Vector3Tuple };
            stations[id] = { ...working, anchor };
            views[id] = { station: id, target: anchor };
        }
    }
    if (wholeEnvelope.min.every(Number.isFinite)) {
        for (const [key, envelope] of [['overview', wholeEnvelope], ['decision-overview', decisionEnvelope]] as const) {
            if (!envelope) continue;
            const allPoints = extentPoints(envelope);
            const id = toPrimitiveCameraViewId(args.scope, key);
            const bounds = { center: envelopeCenter(allPoints), points: allPoints };
            stations[id] = { stationKind: 'framing', framing: {
                framingKind: 'extent', bounds, direction: overviewDirection, padding: camera.base.padding,
            }, fov: camera.base.fov };
            views[id] = { station: id, target: { anchorKind: 'position', position: bounds.center } };
        }
    }
    const settlementEnvelopes = new Map<string, Envelope>();
    for (const step of args.steps) {
        const wheels = toPrimitiveStepWheels(step);
        const target = wheels.find(wheel => wheel.settlementUnit?.participant === 'target');
        const unit = target?.settlementUnit;
        if (!target || !unit) continue;
        for (const power of unit.powers) {
            const id = toPrimitiveCameraViewId(args.scope, `settlement:${target.role}:${power}`);
            const envelope = settlementEnvelopes.get(id) ?? emptyEnvelope();
            const participants = wheels.filter(wheel => wheel.settlementUnit?.unitKey === unit.unitKey
                || (wheel.variant === 'ante' && wheel.role === target.role));
            include(envelope, getFrameGeometry(step, participants, undefined).overviewPoints);
            settlementEnvelopes.set(id, envelope);
        }
    }
    for (const [id, envelope] of settlementEnvelopes) {
        const points = extentPoints(envelope);
        const center = envelopeCenter(points);
        stations[id] = { stationKind: 'framing', framing: {
            framingKind: 'extent', bounds: { center, points },
            direction: placeDirection([1, 0.35, 1]), padding: camera.base.padding,
        }, fov: camera.base.fov };
        views[id] = { station: id, target: { anchorKind: 'position', position: center } };
    }
    const initial = args.steps[0];
    const initialFrame = initial?.keyframes[0];
    if (initial && initialFrame && !initialFrame.booleanRod) {
        const wheels = initialFrame.wheels.filter(wheel => wheel.variant === undefined);
        const receivers = wheels.filter(wheel => wheel.role === 'remainder' || wheel.role === 'quotient');
        if (receivers.length === 2) {
            const points = getFrameGeometry(initial, receivers, undefined).overviewPoints;
            const center = envelopeCenter(points);
            const id = toPrimitiveCameraViewId(args.scope, 'division-receivers');
            stations[id] = { stationKind: 'framing', framing: {
                framingKind: 'extent', bounds: { center, points },
                direction: overviewDirection, padding: camera.base.padding,
            }, fov: camera.base.fov };
            views[id] = { station: id, target: { anchorKind: 'position', position: center } };
        }
        // A repeat comparison turns from Remainder toward the Divisor, so its next dispatch is seen without travel.
        const remainderId = toPrimitiveCameraViewId(args.scope, 'working:remainder:0');
        const remainderStation = stations[remainderId];
        if (remainderStation?.stationKind === 'anchored' && remainderStation.anchor.anchorKind === 'position'
            && wheels.some(wheel => wheel.role === 'divisor')) {
            const divisorKey = toScopedPrimitiveRodKey(toPrimitivePowerConstellationKey({ wheelRole: 'divisor', power: 0 }), args.scope);
            const divisor = makeFrame(initial, wheels, undefined)
                .constellationParametricState?.entityConstellationByKey?.[divisorKey]?.layout?.anchor;
            if (divisor?.anchorKind === 'position') {
                const remainder = remainderStation.anchor.position;
                const id = toPrimitiveCameraViewId(args.scope, 'working:remainder:0:toward-divisor');
                views[id] = { station: remainderId, target: { anchorKind: 'position',
                    position: [0, 1, 2].map(axis => (remainder[axis]! + divisor.position[axis]!) / 2) as Vector3Tuple } };
            }
        }
        const geometry = getFrameGeometry(initial, wheels, undefined);
        const points = geometry.overviewPoints;
        if (points.length > 0) {
            const labels = toPrimitiveCameraViewId(args.scope, 'wheel-labels');
            const center = envelopeCenter(points);
            stations[labels] = { stationKind: 'framing', framing: {
                framingKind: 'extent', bounds: { center, points },
                direction: placeDirection([0, 1, 1]), padding: camera.base.padding,
            }, fov: camera.base.fov };
            views[labels] = { station: labels, target: { anchorKind: 'position', position: center } };
        }
    }
    return { graph: { stations, views, paths: [] }, viewByStepIndex };
}
